import React, { useState } from "react";
import { ChevronDown, ChevronUp, Calendar, X } from "lucide-react";
import { getDefaultDateRange, isPastDaysRange } from "../utils/helpers";

const DateRangePicker = ({
  dateFilterType,
  setDateFilterType,
  singleDate,
  setSingleDate,
  dateRangeStart,
  setDateRangeStart,
  dateRangeEnd,
  setDateRangeEnd,
  availableDates = [],
  dateRangeSummary = null,
  compact = false,
  showLegend = true,
  highlightStyle = "dot", // "dot" (default) or "border" for PayoutRequesters
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Convert available dates to Set for O(1) lookup
  const availableDatesSet = React.useMemo(() => new Set(availableDates), [availableDates]);

  // DEBUG: Log available dates for troubleshooting
  React.useEffect(() => {
    console.log(`🗓️ DateRangePicker: Available dates:`, {
      availableDatesCount: availableDates?.length || 0,
      firstFewDates: availableDates?.slice(0, 5) || [],
      availableDatesSetSize: availableDatesSet.size,
      sampleDateCheck: availableDatesSet.has("2025-09-11")
        ? "HAS 2025-09-11"
        : "MISSING 2025-09-11",
    });
  }, [availableDates, availableDatesSet]);

  // Removed automatic date range initialization - let parent component control initial values

  // Close modal when clicking outside
  const handleBackdropClick = () => {
    setIsOpen(false);
  };

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const dayNames = ["S", "M", "T", "W", "T", "F", "S"];
  const dayNameKeys = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  // Helper to parse 'YYYY-MM-DD' as a local date (no timezone shift)
  function parseLocalDate(dateStr) {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  // Helper to format a Date object as 'YYYY-MM-DD' in local time
  function formatDateLocal(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // Generate calendar days for mini calendar
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
  };

  // Handle date click
  const handleDateClick = (date) => {
    // Convert to YYYY-MM-DD string in local time
    const dateStr = formatDateLocal(date);

    // Allow clicking on any date, but we'll handle no-data cases
    // Removed the availability check: if (!availableDatesSet.has(dateStr)) return;

    if (!dateRangeStart || (dateRangeStart && dateRangeEnd)) {
      // Start new range or single date
      setDateRangeStart(dateStr);
      setDateRangeEnd("");
      setSingleDate(dateStr);
      setDateFilterType("single");
    } else if (dateRangeStart && !dateRangeEnd) {
      // Complete range
      if (dateStr >= dateRangeStart) {
        setDateRangeEnd(dateStr);
        setDateFilterType("range");
        setSingleDate("");
      } else {
        setDateRangeStart(dateStr);
        setDateRangeEnd(dateRangeStart);
        setDateFilterType("range");
        setSingleDate("");
      }
    }
  };

  // Get current filter display text
  const getFilterText = () => {
    if (dateFilterType === "all") return "All Time";

    // Check for quick date ranges first
    if (dateFilterType === "range" && dateRangeStart && dateRangeEnd) {
      if (isQuickRangeActive(7)) return "Past 7 Days";
      if (isQuickRangeActive(30)) return "Past 30 Days";
      if (isQuickRangeActive(90)) return "Past 90 Days";

      // Custom range - show dates
      const startDate = parseLocalDate(dateRangeStart);
      const endDate = parseLocalDate(dateRangeEnd);
      const result = `${startDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })} - ${endDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })}`;

      return result;
    }

    if (singleDate) {
      return parseLocalDate(singleDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }

    if (dateRangeStart) {
      return `From ${parseLocalDate(dateRangeStart).toLocaleDateString(
        "en-US",
        {
          month: "short",
          day: "numeric",
        }
      )}`;
    }

    return "Select Date";
  };

  // Get date styling for mini calendar
  const getDateStyle = (date) => {
    const dateStr = formatDateLocal(date);
    const hasData = availableDatesSet.has(dateStr);
    const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
    const isSelected = dateStr === singleDate;
    const isRangeStart = dateStr === dateRangeStart;
    const isRangeEnd = dateStr === dateRangeEnd;
    const isInRange =
      dateRangeStart &&
      dateRangeEnd &&
      dateStr >= dateRangeStart &&
      dateStr <= dateRangeEnd;
    const todayStr = new Date();
    const todayLocalStr = formatDateLocal(todayStr);
    const isToday = dateStr === todayLocalStr;

    let baseClasses =
      "relative w-8 h-8 text-sm flex items-center justify-center rounded-xl cursor-pointer transition-all duration-200 backdrop-blur-sm";

    if (!isCurrentMonth) {
      // Previous/next month dates - very muted
      baseClasses += " text-gray-600 cursor-default";
    } else if (!hasData) {
      // Current month but no data - still clickable but dimmed
      baseClasses += " text-gray-500 hover:text-gray-400 hover:bg-gray-700/30";
    } else {
      // Current month with data
      if (isSelected || isRangeStart || isRangeEnd) {
        baseClasses +=
          " bg-[#B8460E] text-white shadow-xl border border-[#B8460E]/50";
      } else if (isInRange) {
        baseClasses += " bg-[#B8460E]/30 text-white border border-[#B8460E]/50";
      } else if (isToday) {
        baseClasses +=
          " text-[#B8460E] font-semibold bg-[#B8460E]/10 hover:bg-[#B8460E]/20 border border-[#B8460E]/50";
      } else {
        baseClasses += " text-gray-300 hover:bg-gray-600/50 hover:text-white";
      }

      // Add data indicator based on highlightStyle
      if (!isSelected && !isRangeStart && !isRangeEnd && !isInRange) {
        if (highlightStyle === "border") {
          // Border and background style for PayoutRequesters
          baseClasses += " border border-[#B8460E]/60 bg-[#B8460E]/15 hover:bg-[#B8460E]/25";
        } else {
          // Default dot style for other pages
          baseClasses +=
            " after:absolute after:bottom-0.5 after:left-1/2 after:transform after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:bg-[#B8460E] after:rounded-full";
        }
      }
    }

    return baseClasses;
  };

  // Clear filters
  const clearFilters = () => {
    setDateFilterType("all");
    setSingleDate("");
    setDateRangeStart("");
    setDateRangeEnd("");
    setIsOpen(false);
  };

  // Quick date range handlers
  const setQuickRange = (days) => {
    const dateRange = getDefaultDateRange(days);

    // Always allow setting the range - we'll handle no-data cases in the UI
    setDateRangeStart(dateRange.start);
    setDateRangeEnd(dateRange.end);
    setDateFilterType("range");
    setSingleDate("");
    setIsOpen(false);
  };

  // Handler for yesterday quick select
  const setYesterday = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const yesterdayStr = formatDateLocal(yesterday);

    setSingleDate(yesterdayStr);
    setDateFilterType("single");
    setDateRangeStart("");
    setDateRangeEnd("");
    setIsOpen(false);
  };

  // Check if yesterday is currently selected
  const isYesterdayActive = () => {
    if (dateFilterType !== "single" || !singleDate) return false;

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const expectedYesterday = formatDateLocal(yesterday);

    return singleDate === expectedYesterday;
  };

  // Check if a quick range is currently selected
  const isQuickRangeActive = (days) => {
    if (dateFilterType !== "range" || !dateRangeStart || !dateRangeEnd)
      return false;

    return isPastDaysRange(dateRangeStart, dateRangeEnd, days);
  };

  return (
    <div className="relative w-full sm:w-auto">
      {/* Compact Button */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-full sm:w-auto space-x-2 ${
          compact ? "px-3 py-2 rounded-xl" : "px-6 py-3 rounded-2xl"
        } bg-gray-700/60 backdrop-blur-xl border border-gray-600/50 hover:bg-[#B8460E]/20 hover:border-[#B8460E]/50 transition-all duration-300 text-sm cursor-pointer shadow-xl group relative overflow-hidden`}
      >
        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>

        <Calendar className="h-4 w-4 text-gray-300 group-hover:text-[#B8460E] transition-colors duration-300 relative z-10" />
        <span className="text-white font-medium relative z-10 truncate">
          {getFilterText()}
        </span>
        {dateFilterType !== "all" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              clearFilters();
            }}
            className="ml-1 p-0.5 hover:bg-white/20 rounded relative z-10"
          >
            <X className="h-3 w-3 text-gray-300" />
          </button>
        )}
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-gray-300 group-hover:text-[#B8460E] transition-colors duration-300 relative z-10" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-300 group-hover:text-[#B8460E] transition-colors duration-300 relative z-10" />
        )}
      </div>

      {/* Collapsible Calendar */}
      {isOpen && (
        <>
          {/* Backdrop overlay */}
          <div
            className="fixed inset-0 z-[9998]"
            onClick={handleBackdropClick}
          ></div>

          {/* Calendar Modal */}
          <div className="fixed left-1/2 -translate-x-1/2 top-24 w-[90vw] max-w-sm rounded-2xl bg-gray-800/80 backdrop-blur-xl border border-gray-700 shadow-2xl z-[9999] p-4 animate-fade-in-up sm:absolute sm:top-full sm:left-0 sm:translate-x-0 sm:w-80 sm:max-w-none sm:mt-2">
            {/* Glass morphism background */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl"></div>

            {/* Content */}
            <div className="relative z-10">
              {/* Quick options */}
              <div className="mb-4">
                <div className="text-xs text-gray-300 mb-2 font-medium">
                  Quick Select
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      setDateFilterType("all");
                      setSingleDate("");
                      setDateRangeStart("");
                      setDateRangeEnd("");
                      setIsOpen(false);
                    }}
                    className={`px-3 py-2 text-xs rounded-xl transition-colors backdrop-blur-sm border ${
                      dateFilterType === "all"
                        ? "bg-[#B8460E] text-white border-[#B8460E]/50 shadow-lg"
                        : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border-gray-600/50"
                    }`}
                  >
                    All Time
                  </button>
                  <button
                    onClick={setYesterday}
                    className={`px-3 py-2 text-xs rounded-xl transition-colors backdrop-blur-sm border ${
                      isYesterdayActive()
                        ? "bg-[#B8460E] text-white border-[#B8460E]/50 shadow-lg"
                        : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border-gray-600/50"
                    }`}
                  >
                    Yesterday
                  </button>
                  <button
                    onClick={() => setQuickRange(7)}
                    className={`px-3 py-2 text-xs rounded-xl transition-colors backdrop-blur-sm border ${
                      isQuickRangeActive(7)
                        ? "bg-[#B8460E] text-white border-[#B8460E]/50 shadow-lg"
                        : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border-gray-600/50"
                    }`}
                  >
                    Past 7 Days
                  </button>
                  <button
                    onClick={() => setQuickRange(30)}
                    className={`px-3 py-2 text-xs rounded-xl transition-colors backdrop-blur-sm border ${
                      isQuickRangeActive(30)
                        ? "bg-[#B8460E] text-white border-[#B8460E]/50 shadow-lg"
                        : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border-gray-600/50"
                    }`}
                  >
                    Past 30 Days
                  </button>
                  <button
                    onClick={() => setQuickRange(90)}
                    className={`px-3 py-2 text-xs rounded-xl transition-colors backdrop-blur-sm border ${
                      isQuickRangeActive(90)
                        ? "bg-[#B8460E] text-white border-[#B8460E]/50 shadow-lg"
                        : "bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border-gray-600/50"
                    }`}
                  >
                    Past 90 Days
                  </button>
                </div>
              </div>

              {/* Month navigation */}
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={goToPreviousMonth}
                  className="p-2 hover:bg-gray-600/50 rounded-xl text-gray-300 transition-colors backdrop-blur-sm"
                >
                  <span className="text-lg">‹</span>
                </button>
                <span className="text-sm font-semibold text-white">
                  {monthNames[currentMonth.getMonth()]}{" "}
                  {currentMonth.getFullYear()}
                </span>
                <button
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-gray-600/50 rounded-xl text-gray-300 transition-colors backdrop-blur-sm"
                >
                  <span className="text-lg">›</span>
                </button>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day, index) => (
                  <div
                    key={dayNameKeys[index]}
                    className="h-8 flex items-center justify-center"
                  >
                    <span className="text-xs text-gray-300 font-medium">
                      {day}
                    </span>
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1 mb-3">
                {calendarDays.map((date, index) => (
                  <div
                    key={index}
                    className={getDateStyle(date)}
                    onClick={() => handleDateClick(date)}
                  >
                    {date.getDate()}
                  </div>
                ))}
              </div>

              {/* Data summary */}
              {availableDates.length > 0 && (
                <div className="text-xs text-gray-300 mb-3 p-2 bg-gray-700/50 rounded-xl backdrop-blur-sm">
                  📊 {availableDates.length} days with data available
                </div>
              )}

              {/* Selection info */}
              {(singleDate || (dateRangeStart && dateRangeEnd)) && (
                <div className="pt-3 border-t border-gray-600/50">
                  <div className="text-xs text-gray-300">
                    {singleDate
                      ? `Selected: ${parseLocalDate(
                          singleDate
                        ).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}`
                      : `Range: ${parseLocalDate(
                          dateRangeStart
                        ).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })} - ${parseLocalDate(dateRangeEnd).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                          }
                        )}`}
                  </div>
                </div>
              )}

              {dateRangeStart && !dateRangeEnd && (
                <div className="pt-3 border-t border-gray-600/50">
                  <div className="text-xs text-[#B8460E] bg-[#B8460E]/10 p-2 rounded-xl backdrop-blur-sm">
                    📅 Click another date to complete range
                  </div>
                </div>
              )}

              {/* Legend */}
              {showLegend && (
                <div className="mt-3 pt-3 border-t border-gray-600/50">
                  <div className="text-xs text-gray-300 space-y-1">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded bg-[#B8460E]"></div>
                      <span>Selected</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {highlightStyle === "border" ? (
                        <div className="w-3 h-3 rounded border border-[#B8460E]/60 bg-[#B8460E]/15"></div>
                      ) : (
                        <div className="w-3 h-3 rounded bg-gray-600/50 relative after:absolute after:bottom-0 after:left-1/2 after:transform after:-translate-x-1/2 after:w-1 after:h-1 after:bg-[#B8460E] after:rounded-full"></div>
                      )}
                      <span>Has data</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DateRangePicker;
